import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export type OrgRole = 'owner' | 'admin' | 'member';

export interface Organization {
  orgId: string;
  name: string;
  ownerUserId: string;
  subscriptionTier: string;
  teamMemberLimit: number;
  createdAt: string;
  updatedAt: string;
}

export interface Membership {
  orgId: string;
  userId: string;
  role: OrgRole;
  membershipStatus: 'active' | 'revoked';
  joinedAt: string;
}

export interface MemberWithUser extends Membership {
  email: string;
  givenName: string;
  familyName: string;
}

export interface Invitation {
  orgId: string;
  inviteId: string;
  invitedEmail: string;
  role: OrgRole;
  status: 'pending' | 'accepted' | 'revoked';
  expiresAt: string;
  createdAt: string;
  invitedByEmail: string;
}

export interface OrgMembership {
  org: Organization;
  membership: Membership;
}

export interface GetMeResponse {
  memberships: OrgMembership[];
}

export interface ListMembersResponse {
  members: MemberWithUser[];
}

export interface ListInvitationsResponse {
  invitations: Invitation[];
}

export interface CreateInvitationResponse {
  invitation: Invitation;
}

export interface AcceptInvitationResponse {
  org: Organization;
  membership: Membership;
}

@Injectable({ providedIn: 'root' })
export class OrgService {
  private readonly base = `${environment.apiUrl}/api/v1/organizations`;

  constructor(private readonly http: HttpClient) {}

  getMyOrgs(): Observable<GetMeResponse> {
    return this.http.get<GetMeResponse>(`${this.base}/me`);
  }

  listMembers(orgId: string): Observable<ListMembersResponse> {
    return this.http.get<ListMembersResponse>(`${this.base}/${orgId}/members`);
  }

  listInvitations(orgId: string): Observable<ListInvitationsResponse> {
    return this.http.get<ListInvitationsResponse>(`${this.base}/${orgId}/invitations`);
  }

  createInvitation(orgId: string, email: string, role: OrgRole): Observable<CreateInvitationResponse> {
    return this.http.post<CreateInvitationResponse>(`${this.base}/${orgId}/invitations`, { email, role });
  }

  revokeInvitation(orgId: string, inviteId: string): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.base}/${orgId}/invitations/${inviteId}`);
  }

  acceptInvitation(token: string): Observable<AcceptInvitationResponse> {
    return this.http.post<AcceptInvitationResponse>(`${this.base}/invitations/accept`, { token });
  }

  updateOrgName(orgId: string, name: string): Observable<{ org: Organization }> {
    return this.http.patch<{ org: Organization }>(`${this.base}/${orgId}`, { name });
  }

  updateMemberRole(orgId: string, memberId: string, role: OrgRole): Observable<{ message: string }> {
    return this.http.patch<{ message: string }>(`${this.base}/${orgId}/members/${memberId}`, { role });
  }

  removeMember(orgId: string, memberId: string): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.base}/${orgId}/members/${memberId}`);
  }
}
